import analyticsConfig from '@data/analytics'
import GDPRManager from './GDPRManager'

/**
 * Google Analytics 4 Manager for Club Penguin Client
 * Handles all client-side analytics tracking with GDPR compliance
 */
class GA4Manager {
    constructor() {
        this.initialized = false
        this.enabled = false
        this.config = analyticsConfig
        this.userId = null
        this.sessionId = this.generateSessionId()
        this.gdprManager = null
        // Don't call init() immediately - let it be called later
    }

    /**
     * Initialize GA4 tracking with GDPR compliance
     */
    init() {
        try {
            // Don't initialize if already initialized
            if (this.initialized) {
                return
            }

            // Initialize GDPR manager
            this.gdprManager = GDPRManager.getInstance()

            // Check for analytics consent
            if (!this.gdprManager.canCollectAnalytics()) {
                console.log('[GA4] Analytics disabled - no user consent')
                this.enabled = false
                return
            }

            // Check if gtag is available (should be loaded via script tag)
            if (typeof gtag !== 'undefined') {
                this.initialized = true
                this.enabled = true
                
                // Configure GA4 with enhanced privacy settings
                gtag('config', this.config.measurementId, {
                    debug_mode: this.config.debug,
                    send_page_view: true,
                    anonymize_ip: true,
                    allow_google_signals: false, // Disable advertising features
                    allow_ad_personalization_signals: false,
                    custom_map: this.config.customDimensions
                })

                console.log('[GA4] Analytics initialized with user consent')
            } else {
                console.warn('[GA4] gtag not available - analytics tracking disabled')
            }
        } catch (error) {
            console.error('Failed to initialize GA4Manager:', error)
            this.enabled = false
        }
    }

    /**
     * Generate a unique session ID
     */
    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    }

    /**
     * Set user ID for tracking
     */
    setUserId(userId) {
        if (!this.canTrack()) return

        this.userId = userId
        gtag('config', this.config.measurementId, {
            user_id: userId,
            player_id: userId  // Use player_id for custom dimension
        })
    }

    /**
     * Check if tracking is allowed
     */
    canTrack() {
        return this.initialized && this.enabled && 
               this.gdprManager && this.gdprManager.canCollectAnalytics()
    }

    /**
     * Disable analytics tracking
     */
    disable() {
        this.enabled = false
        console.log('[GA4] Analytics tracking disabled')
    }

    /**
     * Enable analytics tracking (if consent given)
     */
    enable() {
        if (this.gdprManager && this.gdprManager.canCollectAnalytics()) {
            this.enabled = true
            console.log('[GA4] Analytics tracking enabled')
        }
    }

    /**
     * Track generic events
     */
    trackEvent(eventName, parameters = {}) {
        if (!this.canTrack()) return

        try {
            const eventData = {
                session_id: this.sessionId,
                ...parameters
            }

            gtag('event', eventName, eventData)
        } catch (error) {
            console.error('Failed to track event:', error)
        }
    }

    /**
     * Track page/room visits
     */
    trackPageView(roomId, roomName) {
        if (!this.canTrack()) return

        this.trackEvent('page_view', {
            page_title: roomName || `Room ${roomId}`,
            page_location: window.location.href,
            room_id: roomId,
            custom_dimension_3: roomId
        })
    }

    /**
     * Track user login
     */
    trackLogin(userId, method = 'game') {
        this.setUserId(userId)
        this.trackEvent(this.config.events.login, {
            method: method,
            user_id: userId
        })
    }

    /**
     * Track user logout
     */
    trackLogout() {
        this.trackEvent(this.config.events.logout, {
            user_id: this.userId
        })
    }

    /**
     * Track room visits
     */
    trackRoomVisit(roomId, roomName, worldId) {
        this.trackEvent(this.config.events.room_visit, {
            room_id: roomId,
            room_name: roomName,
            world_id: worldId,
            custom_dimension_2: worldId,
            custom_dimension_3: roomId
        })
    }

    /**
     * Track minigame events
     */
    trackMinigameStart(gameId, gameName, roomId) {
        this.trackEvent(this.config.events.game_start, {
            game_id: gameId,
            game_name: gameName,
            room_id: roomId,
            level_name: gameName
        })
    }

    trackMinigameEnd(gameId, gameName, score, duration, outcome) {
        this.trackEvent(this.config.events.game_end, {
            game_id: gameId,
            game_name: gameName,
            score: score,
            duration_seconds: Math.round(duration / 1000),
            outcome: outcome // 'win', 'lose', 'quit'
        })
    }

    /**
     * Track coin earnings and purchases
     */
    trackCoinEarn(amount, source, gameId = null) {
        this.trackEvent(this.config.events.coin_earn, {
            value: amount * this.config.ecommerce.value_multiplier,
            currency: this.config.ecommerce.currency,
            coin_amount: amount,
            source: source,
            game_id: gameId
        })
    }

    trackPurchase(itemId, itemName, itemType, cost, currency = 'coins') {
        this.trackEvent(this.config.events.purchase, {
            transaction_id: `purchase_${Date.now()}_${itemId}`,
            value: cost * this.config.ecommerce.value_multiplier,
            currency: currency,
            items: [{
                item_id: itemId,
                item_name: itemName,
                item_category: itemType,
                price: cost,
                quantity: 1
            }]
        })
    }

    /**
     * Track social interactions
     */
    trackChatMessage(messageType = 'normal', roomId) {
        this.trackEvent(this.config.events.chat_message, {
            message_type: messageType,
            room_id: roomId
        })
    }

    trackBuddyRequest(recipientId) {
        this.trackEvent(this.config.events.buddy_request, {
            recipient_id: recipientId
        })
    }

    trackBuddyAccept(requesterId) {
        this.trackEvent(this.config.events.buddy_accept, {
            requester_id: requesterId
        })
    }

    /**
     * Track clothing and customization
     */
    trackClothingChange(items, actionType = 'equipped') {
        this.trackEvent(this.config.events.clothing_change, {
            action_type: actionType,
            item_count: items.length,
            items: items.map(item => ({
                item_id: item.id,
                item_type: item.type
            }))
        })
    }

    /**
     * Track pet interactions
     */
    trackPetAdopt(petId, petType) {
        this.trackEvent(this.config.events.pet_adopt, {
            pet_id: petId,
            pet_type: petType
        })
    }

    /**
     * Track emote usage
     */
    trackEmoteUse(emoteId, roomId) {
        this.trackEvent(this.config.events.emote_use, {
            emote_id: emoteId,
            room_id: roomId
        })
    }

    /**
     * Track snowball throws
     */
    trackSnowballThrow(targetUserId, roomId) {
        this.trackEvent(this.config.events.snowball_throw, {
            target_user_id: targetUserId,
            room_id: roomId,
            has_target: !!targetUserId
        })
    }

    /**
     * Track igloo visits and updates
     */
    trackIglooVisit(iglooOwnerId, iglooType) {
        this.trackEvent(this.config.events.igloo_visit, {
            owner_id: iglooOwnerId,
            igloo_type: iglooType
        })
    }

    /**
     * Track errors and exceptions
     */
    trackError(error, context = '') {
        this.trackEvent('exception', {
            description: error.message || error,
            fatal: false,
            context: context
        })
    }

    /**
     * Track user engagement metrics
     */
    trackEngagement(engagementType, value = 1) {
        this.trackEvent('user_engagement', {
            engagement_type: engagementType,
            engagement_value: value
        })
    }

    /**
     * Set custom user properties
     */
    setUserProperties(properties) {
        if (!this.canTrack()) return

        try {
            gtag('config', this.config.measurementId, {
                custom_map: {
                    ...this.config.customDimensions,
                    ...properties
                }
            })
        } catch (error) {
            console.error('Failed to set user properties:', error)
        }
    }

    /**
     * Track session start
     */
    trackSessionStart(worldId, userId) {
        this.trackEvent('session_start', {
            session_id: this.sessionId,
            world_id: worldId,
            user_id: userId,
            custom_dimension_2: worldId
        })
    }

    /**
     * Track session end
     */
    trackSessionEnd(duration) {
        this.trackEvent('session_end', {
            session_id: this.sessionId,
            session_duration: Math.round(duration / 1000),
            user_id: this.userId
        })
    }
}

// Create global instance
window.GA4Manager = new GA4Manager()

export default window.GA4Manager